/* eslint-disable */
// NPM Imports
import moment from "moment";
import store from "../store";
import { GRID } from "../constants";

// Internal Imports
import * as ProgramUtils from "./programUtils";

// Destructuring
const {
  CHARGE_TYPE_METHODS,
  CONVERSION_FACTORS,
  DEALTABLE_COLUMNS,
  PRICING_METHODS,
  RECURRENCE,
  RECURRING_PERIOD,
} = GRID;

const { roundUpByDecimal, roundUpByNumber, roundUpByUnit } = ProgramUtils;

// Set the basePrice of dealTable according to the pricingType Selected
const _calculateBasePrice = data => {
  if (data.price) {
    const units = data.default_qty > 0 ? data.default_qty : 1;
    const { basePrice, bundles, range, type } = data.price;
    const MAX_QTY = 1000000000000000000000;
    let finalPrice = 0;
    let price = null;
    let qty = units;
    let sum = 0;
    // Set values based on price type
    switch (type) {
      case PRICING_METHODS.VOLUME_PRICING:
        [price] = range.filter(num => num.min <= units && num.max >= units);
        if (price) {
          finalPrice = (
            (units * price.value + basePrice) /
            (units || 1)
          ).toFixed(2); // units can be empty, so infinity might happen
          return JSON.parse(finalPrice);
        }
        break;
      case PRICING_METHODS.STEP_TIER_PRICING:
        [price] = range.filter(num => num.min <= units && num.max >= units);
        if (price) return JSON.parse(price.value + basePrice);
        break;
      case PRICING_METHODS.TIER_PRICING:
        range.every(item => {
          const correction = item.min === 0 ? 0 : 1; // since the range starts from 0 for the first range
          const maxRange = item.max ? item.max : MAX_QTY;
          const items = maxRange - item.min + correction;
          if (qty >= items) {
            sum += items * item.value;
            qty -= items;
            return true;
          }
          sum += qty * item.value;
          return false;
        });
        finalPrice = ((sum + basePrice) / (units || 1)).toFixed(2); // units can be empty, so infinity might happen
        return JSON.parse(finalPrice);
      case PRICING_METHODS.BUNDLE_PRICING:
      case PRICING_METHODS.NON_LINEAR:
        finalPrice =
          bundles.reduce(
            (sumValue, item) => sumValue + item.price * item.months,
            0,
          ) + basePrice; // (Price * Months + BasePrice) in a loop
        return JSON.parse(finalPrice);
      case PRICING_METHODS.PERCENTAGE_OF_TOTAL:
        return 0;
      default:
        return (data.price && parseFloat(data.price.value)) || 0;
    }
  }
  return (data.price && parseFloat(data.price.value)) || 0;
};

const _calculateEndDate = (startDate, sellingTerm, recurringPeriod) => {
  let endDate = "";
  switch (recurringPeriod) {
    case RECURRING_PERIOD.DAILY:
      endDate = moment(startDate)
        .add(sellingTerm, "d")
        .format();
      break;
    case RECURRING_PERIOD.WEEKLY:
      endDate = moment(startDate)
        .add(7 * sellingTerm, "d")
        .format();
      break;
    case RECURRING_PERIOD.MONTHLY:
      endDate = moment(startDate)
        .add(sellingTerm, "M")
        .format();
      break;
    case RECURRING_PERIOD.QUARTERLY:
      endDate = moment(startDate)
        .add(3 * sellingTerm, "M")
        .format();
      break;
    case RECURRING_PERIOD.HALF_YEARLY:
      endDate = moment(startDate)
        .add(6 * sellingTerm, "M")
        .format();
      break;
    case RECURRING_PERIOD.YEARLY:
      endDate = moment(startDate)
        .add(12 * sellingTerm, "M")
        .format();
      break;
    default:
      endDate = moment(startDate).format();
      break;
  }
  return endDate;
};

const _calculateConversionFactors = (
  chargeType,
  chargeFrequency,
  recurringPeriod,
) => {
  if (
    chargeType === CHARGE_TYPE_METHODS.RECURRING ||
    chargeType === CHARGE_TYPE_METHODS.RECURRING_USAGE
  ) {
    return (
      CONVERSION_FACTORS[RECURRENCE[chargeFrequency]][
        RECURRENCE[recurringPeriod]
      ] || 1
    );
  }
  return 1;
};

// Funtion to fill all the columns needed for dealtable
export const mapProductToDealTable = (items, fieldMapIndex) => {
  const { quoteDetails } = store.getState().QuotesStore;
  const dealtableRow = [];
  const defaultDiscountAmount = 0;
  const defaultDiscountPercentage = 0;
  const defaultMinQty = 0;
  const defaultQty = 1;
  const defaultSellingTerm = 1;
  const defaultSkuQty = 1;
  const today = new Date().toISOString();
  items.forEach(item => {
    const price = _calculateBasePrice(item);
    const chargeType = item.charge_type;
    // Calculate conversionFactor value base on the following arguments
    const conversionFactor = _calculateConversionFactors(
      chargeType,
      item.frequency,
      item.recurring_period,
    );
    const obj = {};
    obj.index = item.id;
    // Make the row dirty, on save only dirty rows are updated
    obj.isDirty = true;
    // To hold pricingType metaData
    obj.metaData = item.price;
    // Map product flat values to FLD values
    Object.keys(fieldMapIndex).forEach(key => {
      switch (key) {
        case DEALTABLE_COLUMNS.ADDITIONAL_DISCOUNT_PERCENT:
          obj[fieldMapIndex[key]] = 0;
          break;

        case DEALTABLE_COLUMNS.BILLING_FREQUENCY:
          obj[fieldMapIndex[key]] = item.billing_frequency;
          break;

        case DEALTABLE_COLUMNS.CHARGE_FREQUENCY:
          obj[fieldMapIndex[key]] = item.frequency;
          break;

        case DEALTABLE_COLUMNS.CHARGE_TYPE:
          obj[fieldMapIndex[key]] = chargeType;
          break;

        case DEALTABLE_COLUMNS.DISCOUNT_AMT:
          obj[fieldMapIndex[key]] = defaultDiscountAmount;
          break;

        case DEALTABLE_COLUMNS.DISC_PERCENT:
          obj[fieldMapIndex[key]] = defaultDiscountPercentage;
          break;

        case DEALTABLE_COLUMNS.END_DATE:
          {
            const date = quoteDetails.service_start || today;
            obj[fieldMapIndex[key]] = _calculateEndDate(
              date,
              item.selling_term || defaultSellingTerm,
              item.recurring_period,
            );
          }
          break;

        case DEALTABLE_COLUMNS.EXT_BASE_PRICE:
          {
            let QTY = 1;
            if (
              [
                PRICING_METHODS.TIER_PRICING,
                PRICING_METHODS.STEP_TIER_PRICING,
                PRICING_METHODS.VOLUME_PRICING,
              ].includes(item.price.type) ||
              (item.price.type === PRICING_METHODS.FLAT_PRICE &&
                item.quantity_uom)
            ) {
              QTY = item.default_qty;
            }
            obj[fieldMapIndex[key]] =
              price *
              QTY *
              (item.selling_term || defaultSellingTerm) *
              conversionFactor;
            // Override the ext base price value in case of DIY Pricing
            if (item.price.type === PRICING_METHODS.DIY_PRICING) {
              obj[fieldMapIndex[key]] = item.price.minPrice;
            }
          }
          break;

        case DEALTABLE_COLUMNS.DISCOUNTED_PRICE:
          {
            let QTY = 1;
            if (
              [
                PRICING_METHODS.TIER_PRICING,
                PRICING_METHODS.STEP_TIER_PRICING,
                PRICING_METHODS.VOLUME_PRICING,
              ].includes(item.price.type) ||
              (item.price.type === PRICING_METHODS.FLAT_PRICE &&
                item.quantity_uom)
            ) {
              QTY = item.default_qty;
            }

            const extBasePrice =
              price *
              QTY *
              (item.selling_term || defaultSellingTerm) *
              conversionFactor;
            obj[fieldMapIndex[key]] =
              extBasePrice * (1 - defaultDiscountPercentage / 100);
          }
          break;

        case DEALTABLE_COLUMNS.NET_PRICE:
          {
            const extBasePrice =
              obj[fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE]];
            obj[fieldMapIndex[key]] = extBasePrice - defaultDiscountAmount;
          }
          break;

        case DEALTABLE_COLUMNS.PRICE:
          obj[fieldMapIndex[key]] = price;
          break;

        case DEALTABLE_COLUMNS.PRICELIST_ITEM:
          obj[fieldMapIndex[key]] = item.id;
          break;

        case DEALTABLE_COLUMNS.PRODUCT:
          obj[fieldMapIndex[key]] = item.product.id;
          break;

        case DEALTABLE_COLUMNS.PRODUCT_CODE:
          obj[fieldMapIndex[key]] = item.product.code;
          break;

        case DEALTABLE_COLUMNS.PRODUCT_DESC:
          obj[fieldMapIndex[key]] = item.product.description;
          break;

        case DEALTABLE_COLUMNS.PRODUCT_NAME:
          obj[fieldMapIndex[key]] = item.product.name;
          break;

        case DEALTABLE_COLUMNS.PTYPE:
          obj[fieldMapIndex[key]] = item.product.type;
          break;

        case DEALTABLE_COLUMNS.QUANTITY:
          if (
            [
              PRICING_METHODS.TIER_PRICING,
              PRICING_METHODS.STEP_TIER_PRICING,
              PRICING_METHODS.VOLUME_PRICING,
            ].includes(item.price.type) ||
            (item.price.type === PRICING_METHODS.FLAT_PRICE &&
              item.quantity_uom)
          ) {
            obj[fieldMapIndex[key]] = item.default_qty;
          } else {
            obj[fieldMapIndex[key]] = defaultQty;
          }
          break;

        case DEALTABLE_COLUMNS.MIN_QTY:
          obj[fieldMapIndex[key]] = item.min_qty || defaultMinQty;
          break;

        case DEALTABLE_COLUMNS.SELLING_TERM:
          obj[fieldMapIndex[key]] = item.selling_term || defaultSellingTerm;
          break;

        case DEALTABLE_COLUMNS.SKU:
          obj[fieldMapIndex[key]] = item.product.sku;
          break;

        case DEALTABLE_COLUMNS.SKU_QUANTITY:
          if (
            [
              PRICING_METHODS.BUNDLE_PRICING,
              PRICING_METHODS.NON_LINEAR,
              PRICING_METHODS.PERCENTAGE_OF_TOTAL,
              PRICING_METHODS.DIY_PRICING,
            ].includes(item.price.type) ||
            (item.price.type === PRICING_METHODS.FLAT_PRICE &&
              !item.quantity_uom)
          ) {
            obj[fieldMapIndex[key]] = item.default_qty;
          } else {
            obj[fieldMapIndex[key]] = defaultSkuQty;
          }
          break;

        case DEALTABLE_COLUMNS.START_DATE:
          obj[fieldMapIndex[key]] = today;
          break;

        case DEALTABLE_COLUMNS.UOM:
          obj[fieldMapIndex[key]] = item.uom;
          break;

        case DEALTABLE_COLUMNS.REMARKS:
          obj[fieldMapIndex[key]] = item.remarks;
          break;

        case DEALTABLE_COLUMNS.QUANTITY_EDITABLE:
          obj[fieldMapIndex[key]] = item.qty_editable;
          break;

        case DEALTABLE_COLUMNS.QUANTITY_UOM:
          obj[fieldMapIndex[key]] = item.quantity_uom;
          break;

        case DEALTABLE_COLUMNS.IS_SELLINGTERM_EDITABLE:
          obj[fieldMapIndex[key]] = item.sellingterm_editable;
          break;

        case DEALTABLE_COLUMNS.RECURRING_PERIOD:
          obj[fieldMapIndex[key]] = item.recurring_period;
          break;

        case DEALTABLE_COLUMNS.MAX_USAGE:
          obj[fieldMapIndex[key]] = item.max_usage;
          break;

        case DEALTABLE_COLUMNS.MAX_USAGE_UOM:
          obj[fieldMapIndex[key]] = item.max_usage_uom;
          break;

        case DEALTABLE_COLUMNS.BILLING_FREQUENCY_EDITABLE:
          obj[fieldMapIndex[key]] = item.billing_frequency_editable;
          break;

        case DEALTABLE_COLUMNS.SKU_QUANTITY_EDITABLE:
          obj[fieldMapIndex[key]] = item.sku_quantity_editable;
          break;

        case DEALTABLE_COLUMNS.COMPUTED_FAMILY:
          obj[fieldMapIndex[key]] = item.product.computed_family;
          break;

        default:
          break;
      }
    });
    dealtableRow.push(obj);
  });
  return dealtableRow;
};

export const mapReactionsOnCellChange = (
  fieldName,
  row,
  rowData,
  fieldMapIndex,
) => {
  const { index } = row;
  const clonedRow = { ...row };
  const dealTableItems = rowData;
  const priceMatrixFlag = row.metaData;
  const rowIndex = dealTableItems.findIndex(key => key.index === index);

  // Current values of the column
  let price = 0;
  const addtionalDiscountPercentage =
    clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.ADDITIONAL_DISCOUNT_PERCENT]];
  const chargeFrequency =
    clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.CHARGE_FREQUENCY]];
  const chargeType = clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.CHARGE_TYPE]];
  let basePrice = clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.PRICE]];
  const extBasePrice =
    clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE]];
  const discountAmount =
    clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNT_AMT]];
  const discountPercentage =
    clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.DISC_PERCENT]];
  const discountedPrice =
    clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNTED_PRICE]];
  let qty = clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.QUANTITY]];
  const sellingTerm = clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.SELLING_TERM]];
  const startDate = clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.START_DATE]];
  const recurringPeriod =
    clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.RECURRING_PERIOD]];
  const skuQty = clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.SKU_QUANTITY]];
  const conversionFactor = _calculateConversionFactors(
    chargeType,
    chargeFrequency,
    recurringPeriod,
  );

  switch (fieldName) {
    case DEALTABLE_COLUMNS.QUANTITY:
      // If DIY Pricing then side-effects are not applicable
      if (clonedRow.metaData.type === PRICING_METHODS.DIY_PRICING) break;

      // Side Effects starts
      if (
        priceMatrixFlag &&
        priceMatrixFlag.type === PRICING_METHODS.STEP_TIER_PRICING
      ) {
        ({ basePrice, qty, price } = priceMatrixFlag);
        // Code to update QUANTITY
        clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.QUANTITY]] = qty;

        // Code to update BASE PRICE
        basePrice = price;
        clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.PRICE]] = price;
      } else if (priceMatrixFlag && priceMatrixFlag.price) {
        ({ basePrice, qty, price } = priceMatrixFlag);
        // Code to update QUANTITY
        clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.QUANTITY]] = qty;

        // Code to update BASE PRICE
        basePrice = (basePrice + price * qty) / qty;
        clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.PRICE]] = basePrice;
      } else if (priceMatrixFlag.isAdhoc) {
        // Code to update BASE PRICE
        basePrice = priceMatrixFlag.value;
        clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.PRICE]] = basePrice;
      } else {
        // Code to update BASE PRICE
        clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.PRICE]] = basePrice;
      }

      // Code to update EXTENDED BASE PRICE
      if (priceMatrixFlag.type === PRICING_METHODS.STEP_TIER_PRICING) {
        clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE]] =
          basePrice * sellingTerm * skuQty * conversionFactor;
      } else {
        clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE]] =
          basePrice * qty * sellingTerm * skuQty * conversionFactor;
      }

      // Code to update DISCOUNT AMOUNT
      clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNT_AMT]] =
        (clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE]] *
          discountPercentage) /
        100;

      // Code to update DISCOUNTED PRICE
      clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNTED_PRICE]] =
        clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE]] *
        (1 - discountPercentage / 100);

      // Code to update NET PRICE
      clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.NET_PRICE]] =
        clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNTED_PRICE]] -
        (clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNTED_PRICE]] *
          addtionalDiscountPercentage) /
          100;

      // To retain used entered efforts value, a hidden column is used
      if (
        priceMatrixFlag &&
        priceMatrixFlag.type === PRICING_METHODS.BUNDLE_PRICING
      ) {
        clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.BUNDLE_QUANTITY]] =
          row.metaData;
      }
      break;

    case DEALTABLE_COLUMNS.SELLING_TERM:
      // Code to update END DATE
      clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.END_DATE]] = _calculateEndDate(
        startDate,
        sellingTerm,
        recurringPeriod,
      );

      // If DIY Pricing then side-effects are not applicable
      if (clonedRow.metaData.type === PRICING_METHODS.DIY_PRICING) break;

      // Code to update EXTENDED BASE PRICE
      if (
        clonedRow.metaData.type === PRICING_METHODS.STEP_TIER_PRICING // for step-tier EBP = BP * ST
      ) {
        clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE]] =
          basePrice * sellingTerm * skuQty * conversionFactor;
      } else {
        clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE]] =
          basePrice * qty * sellingTerm * skuQty * conversionFactor;
      }

      // Code to update DISCOUNT AMOUNT
      clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNT_AMT]] =
        (clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE]] *
          discountPercentage) /
        100;

      // Code to update DISCOUNTED PRICE
      clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNTED_PRICE]] =
        clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE]] *
        (1 - discountPercentage / 100);

      // Code to update NET PRICE
      clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.NET_PRICE]] =
        clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNTED_PRICE]] -
        (clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNTED_PRICE]] *
          addtionalDiscountPercentage) /
          100;

      break;

    case DEALTABLE_COLUMNS.DISCOUNT_AMT:
      // Code to update DISCOUNT PERCENTAGE
      clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.DISC_PERCENT]] =
        (discountAmount / extBasePrice) * 100;

      // Code to update NET PRICE
      clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.NET_PRICE]] =
        extBasePrice - discountAmount;
      break;

    case DEALTABLE_COLUMNS.DISC_PERCENT:
      {
        const discountAmnt = extBasePrice * (1 - discountPercentage / 100);

        // Code to update DISCOUNT AMOUNT
        clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNT_AMT]] = discountAmnt;

        // Code to update DISCOUNTED PRICE
        clonedRow[
          fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNTED_PRICE]
        ] = discountAmnt;

        // Code to update NET PRICE
        clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.NET_PRICE]] =
          discountAmnt * (1 - addtionalDiscountPercentage / 100);
      }
      break;

    case DEALTABLE_COLUMNS.ADDITIONAL_DISCOUNT_PERCENT:
      // Code to update NET PRICE
      clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.NET_PRICE]] =
        discountedPrice * (1 - addtionalDiscountPercentage / 100);
      break;

    case DEALTABLE_COLUMNS.START_DATE:
      // Code to update END DATE
      clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.END_DATE]] = _calculateEndDate(
        startDate,
        sellingTerm,
        recurringPeriod,
      );
      break;

    case DEALTABLE_COLUMNS.SKU_QUANTITY:
      // If DIY Pricing then side-effects are not applicable
      if (clonedRow.metaData.type === PRICING_METHODS.DIY_PRICING) break;

      // Code to update EXTENDED BASE PRICE
      if (
        clonedRow.metaData.type === PRICING_METHODS.STEP_TIER_PRICING // for step-tier EBP = BP * ST
      ) {
        clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE]] =
          basePrice * sellingTerm * skuQty * conversionFactor;
      } else if (
        clonedRow.metaData &&
        clonedRow.metaData.type === PRICING_METHODS.NON_LINEAR // for NON_LINEAR EBP = [(Price * PM * SKUQuant{depends on the multiplySku flag}) + (Price * PM) + ....] + (BP * SKUQuant)
      ) {
        let extendedBasePrice = 0;
        const { basePrice: bundleBasePrice } = clonedRow.metaData;
        const skuQuantity =
          clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.SKU_QUANTITY]];
        clonedRow.metaData.bundles.forEach(bundle => {
          const { months, price: bundlePrice, multiplySku } = bundle;
          extendedBasePrice +=
            months * bundlePrice * (multiplySku ? skuQuantity : 1);
        });
        extendedBasePrice += bundleBasePrice * skuQuantity;
        clonedRow[
          fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE]
        ] = extendedBasePrice;

        // Code to update BASE PRICE
        // maths for this not decided yet
      } else {
        clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE]] =
          basePrice * qty * sellingTerm * skuQty * conversionFactor;
      }

      // Code to update DISCOUNT AMOUNT
      clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNT_AMT]] =
        (clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE]] *
          discountPercentage) /
        100;

      // Code to update DISCOUNTED PRICE
      clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNTED_PRICE]] =
        clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE]] *
        (1 - discountPercentage / 100);

      // Code to update NET PRICE
      clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.NET_PRICE]] =
        clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNTED_PRICE]] -
        (clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNTED_PRICE]] *
          addtionalDiscountPercentage) /
          100;
      break;

    case DEALTABLE_COLUMNS.EXT_BASE_PRICE:
      // Code to update EXTENDED BASE PRICE
      clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE]] = extBasePrice;

      // Code to update DISCOUNT AMOUNT
      clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNT_AMT]] =
        (clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE]] *
          discountPercentage) /
        100;

      // Code to update DISCOUNTED PRICE
      clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNTED_PRICE]] =
        clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.EXT_BASE_PRICE]] *
        (1 - discountPercentage / 100);

      // Code to update NET PRICE
      clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.NET_PRICE]] =
        clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNTED_PRICE]] -
        (clonedRow[fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNTED_PRICE]] *
          addtionalDiscountPercentage) /
          100;
      break;

    default:
      break;
  }

  // Below code replaces the old row with new
  dealTableItems.splice(rowIndex, 1, clonedRow);
  return dealTableItems;
};

export const handlePercentageOfTotal = (rows, fieldMapIndex) => {
  //* Filter out the products which are percentageOfTotalProduct
  const percentageOfTotalProduct = rows.filter(
    row => row.rowMeta.pricingData.type === PRICING_METHODS.PERCENTAGE_OF_TOTAL,
  );

  if (percentageOfTotalProduct.length > 0) {
    percentageOfTotalProduct.forEach(product => {
      const {
        destinationColumn,
        sourceColumn,
        value,
      } = product.rowMeta.pricingData;

      //* Filter out the product Ids which are mapped to percentageOfTotalProduct
      const potProductIds =
        product.rowMeta.pricingData.priceListItems &&
        product.rowMeta.pricingData.priceListItems.map(
          item => item.pricelist_item_id,
        );
      //* Perform POT logic only if items are present
      if (potProductIds) {
        //* Filter out the products which are mapped to percentageOfTotalProduct
        const productsAttachedToPOT = rows.filter(row =>
          potProductIds.includes(row.rowMeta.index),
        );
        const totalValue = productsAttachedToPOT.reduce(
          (total, row) =>
            total + row.rowData[fieldMapIndex[sourceColumn]].cellData,
          0,
        );
        const index = rows.findIndex(
          item => item.rowMeta.index === product.rowMeta.index,
        );
        const extBasePrice = (totalValue * value) / 100;
        rows[index].rowData[
          fieldMapIndex[destinationColumn]
        ].cellData = extBasePrice;

        // Side effect handling
        const discountPercentage =
          rows[index].rowData[fieldMapIndex[DEALTABLE_COLUMNS.DISC_PERCENT]]
            .cellData;
        const discountedPrice = extBasePrice * (1 - discountPercentage / 100);
        const addtionalDiscountPercentage =
          rows[index].rowData[
            fieldMapIndex[DEALTABLE_COLUMNS.ADDITIONAL_DISCOUNT_PERCENT]
          ].cellData;
        // Update discounted price
        rows[index].rowData[
          fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNTED_PRICE]
        ].cellData = extBasePrice * (1 - discountPercentage / 100);

        rows[index].rowData[
          fieldMapIndex[DEALTABLE_COLUMNS.NET_PRICE]
        ].cellData =
          discountedPrice -
          (discountedPrice * addtionalDiscountPercentage) / 100;
      }
    });
  }
  return rows;
};
// export const handlePercentageOfTotal = (rows, fieldMapIndex, roundingType) => {
//   //* Filter out the products which are percentageOfTotalProduct
//   const percentageOfTotalProduct = rows.filter(
//     row => row.metaData.type === PRICING_METHODS.PERCENTAGE_OF_TOTAL,
//   );

//   if (percentageOfTotalProduct.length > 0) {
//     percentageOfTotalProduct.forEach(product => {
//       const { destinationColumn, sourceColumn, value } = product.metaData;

//       //* Filter out the product Ids which are mapped to percentageOfTotalProduct
//       const potProductIds =
//         product.metaData.priceListItems &&
//         product.metaData.priceListItems.map(item => item.product_id);

//       //* Perform POT logic only if items are present
//       if (potProductIds) {
//         //* Filter out the products which are mapped to percentageOfTotalProduct
//         const productsAttachedToPOT = rows.filter(row =>
//           potProductIds.includes(row[fieldMapIndex[DEALTABLE_COLUMNS.PRODUCT]]),
//         );

//         const totalValue = productsAttachedToPOT.reduce(
//           (total, row) => total + row[fieldMapIndex[sourceColumn]],
//           0,
//         );
//         const index = rows.findIndex(item => item.index === product.index);

//         // Update the column value of the row
//         const extBasePrice = (totalValue * value) / 100;
//         rows[index][fieldMapIndex[destinationColumn]] = extBasePrice;

//         // Side effect handling
//         const discountPercentage =
//           rows[index][fieldMapIndex[DEALTABLE_COLUMNS.DISC_PERCENT]];
//         const discountedPrice = extBasePrice * (1 - discountPercentage / 100);
//         const addtionalDiscountPercentage =
//           rows[index][
//             fieldMapIndex[DEALTABLE_COLUMNS.ADDITIONAL_DISCOUNT_PERCENT]
//           ];

//         // Update discounted price
//         rows[index][fieldMapIndex[DEALTABLE_COLUMNS.DISCOUNTED_PRICE]] =
//           extBasePrice * (1 - discountPercentage / 100);

//         // Update net price
//         rows[index][fieldMapIndex[DEALTABLE_COLUMNS.NET_PRICE]] =
//           discountedPrice -
//           (discountedPrice * addtionalDiscountPercentage) / 100;
//       }
//     });

//     if (roundingType) {
//       const netPriceFld = fieldMapIndex[DEALTABLE_COLUMNS.NET_PRICE];
//       // Update the net amount value based on the round Off type selection
//       rows.forEach(row => {
//         switch (roundingType) {
//           case "decimal":
//             row[netPriceFld] = roundUpByDecimal(row[netPriceFld], 1);
//             break;
//           case "unit":
//             row[netPriceFld] = roundUpByUnit(row[netPriceFld]);
//             break;
//           case "tens":
//             row[netPriceFld] = roundUpByNumber(row[netPriceFld], 10);
//             break;
//           default:
//             break;
//         }
//       });
//     }
//   }
//   return rows;
// };
